import type { Dispatch, SetStateAction } from 'react';
import { flushSync } from 'react-dom';
import { Connection, Edge, Node } from 'reactflow';
import { FlowService } from '../../service/FlowService';
import useFlowsStore from '../../store/storeFlows';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import { useVersionControlStore } from '../../store/storeVersionControl';
import { deepEqual } from '../deepEqualData';
import { nodeSchema } from '../myFunctionsCoreDisplay';
import { handleChanges } from './changeTypes';
import { canUnlockNonCriticalPatch } from './flowCanvasPatchGate';
import type { FlowTriggerAutosaveData } from './flowTriggerAutosave.types';

type SaveCacheEntry = {
  lastData: Record<string, unknown> | null;
  lastServerAcknowledgedData: Record<string, unknown> | null;
  timer: ReturnType<typeof setTimeout> | null;
  timestamp: number;
};

type AutoSavePayload = {
  setEdges?: Dispatch<SetStateAction<Edge[]>>;
  setNodeState: Dispatch<SetStateAction<Node[]>>;
  id: string;
  flowTriggerData?: FlowTriggerAutosaveData;
  flowId?: string;
  objectData?: Record<string, unknown>;
  setSchema: (
    schema: Record<string, { type: string; required: boolean }>,
  ) => void;
  newEdges: Edge[];
  changeType?: string;
  connectData?: Edge;
  deleteEdges?: Edge[];
  updateEdge?: {
    oldEdge: Edge;
    newConnection: Connection;
  };
  objectCallerData?: { id: string; text: { id: string; text: string }[] }[];
  setSaving: (saving: {
    lastSaved?: string;
    status: string;
    finished?: boolean;
    flowId?: string;
  }) => void;
  versionId?: string;
};

const saveCache = new Map<string, SaveCacheEntry>();
const DEBOUNCE_DELAY = 800;
const CACHE_CLEANUP_INTERVAL = 60000;
const CACHE_MAX_AGE = 300000;

const CRITICAL_CHANGE_TYPES = [
  'execute',
  'delete-edges',
  'update-edge',
  'connect',
  'save-logs',
];

type DefaultPendingEntry = {
  timer: ReturnType<typeof setTimeout> | null;
  mergedObjectData: Record<string, unknown>;
  generation: number;
  flushArgs: Omit<AutoSavePayload, 'objectData'> | null;
};

const defaultPendingByNode = new Map<string, DefaultPendingEntry>();

const defaultPendingKey = (flowId: string, nodeId: string) =>
  `${flowId}:${nodeId}`;

let cleanupIntervalId: ReturnType<typeof setInterval> | null = null;

const startCacheCleanup = () => {
  if (cleanupIntervalId) return;

  cleanupIntervalId = setInterval(() => {
    const now = Date.now();
    const entriesToDelete: string[] = [];

    saveCache.forEach((entry, key) => {
      if (now - entry.timestamp > CACHE_MAX_AGE) {
        if (entry.timer) {
          clearTimeout(entry.timer);
        }
        entriesToDelete.push(key);
      }
    });

    entriesToDelete.forEach(key => saveCache.delete(key));
  }, CACHE_CLEANUP_INTERVAL);
};

startCacheCleanup();

type HandleChangesPersistResult = {
  nds: Node[];
  dataChangedNodeIds: string[];
};

const persistPrimaryAndPropagatedNodes = async ({
  flowId,
  primaryId,
  primaryData,
  primaryChangeType,
  nds,
  dataChangedNodeIds,
  versionId,
  setSaving,
}: {
  flowId: string;
  primaryId: string;
  primaryData: Record<string, unknown>;
  primaryChangeType: string;
  nds: Node[];
  dataChangedNodeIds: string[];
  versionId?: string;
  setSaving: AutoSavePayload['setSaving'];
}): Promise<boolean> => {
  const saving = {
    finished: false,
    lastSaved: new Date().toISOString(),
    status: 'loading',
    flowId,
  };
  setSaving(saving);
  try {
    await FlowService.updateNodeData(flowId, {
      id: primaryId,
      data: { ...primaryData, isEmptyState: false },
      changeType: primaryChangeType,
      versionId,
    });
    const secondaryIds = (dataChangedNodeIds ?? []).filter(
      nid => nid !== primaryId,
    );
    if (secondaryIds.length > 0) {
      await Promise.all(
        secondaryIds.map(nid => {
          const n = nds.find(x => x.id === nid);
          if (!n?.data || typeof n.data !== 'object') {
            return Promise.resolve();
          }
          return FlowService.updateNodeData(flowId, {
            id: nid,
            data: {
              ...(n.data as Record<string, unknown>),
              isEmptyState: false,
            },
            changeType: 'default',
            versionId,
          });
        }),
      );
    }
    setSaving({
      finished: true,
      lastSaved: new Date().toISOString(),
      status: 'saved',
      flowId,
    });
    return true;
  } catch {
    setSaving({
      finished: false,
      lastSaved: new Date().toISOString(),
      status: 'error',
      flowId,
    });
    return false;
  }
};

const applyHandleChangesToNodes = (
  payload: AutoSavePayload,
): HandleChangesPersistResult => {
  const {
    setNodeState,
    id,
    flowTriggerData,
    objectData,
    setSchema,
    newEdges,
    changeType = 'default',
    connectData,
    deleteEdges,
    updateEdge,
    objectCallerData,
  } = payload;
  const resolvedObjectData = objectData ?? {};
  const edges = [...newEdges];

  let result: HandleChangesPersistResult = { nds: [], dataChangedNodeIds: [] };

  const applyGraph = () => {
    setNodeState((node: Node[]) => {
      const validNodes = node.filter(n => n !== undefined && n !== null);

      const { nds, dataChangedNodeIds } = handleChanges({
        id,
        objectData: resolvedObjectData as never,
        changeType,
        connectData,
        deleteEdges,
        updateEdge,
        objectCallerData,
        edges,
        flowTriggerData,
        node: validNodes,
      });

      const validResult = nds.filter(
        (n: Node) => n !== undefined && n !== null,
      );
      nodeSchema(validResult, setSchema);
      result = {
        nds: validResult,
        dataChangedNodeIds: dataChangedNodeIds ?? [],
      };
      return validResult;
    });
  };

  if (changeType === 'delete-edges') {
    applyGraph();
  } else {
    flushSync(applyGraph);
  }

  return result;
};

type RevertFlushBase = {
  setNodeState: AutoSavePayload['setNodeState'];
  id: AutoSavePayload['id'];
  flowTriggerData?: AutoSavePayload['flowTriggerData'];
  flowId?: string;
  setSchema: AutoSavePayload['setSchema'];
  newEdges: AutoSavePayload['newEdges'];
  setSaving: AutoSavePayload['setSaving'];
  versionId?: string;
};

const revertOptimisticNodeData = (
  flushBase: RevertFlushBase,
  snapshot: Record<string, unknown>,
) => {
  applyHandleChangesToNodes({
    setNodeState: flushBase.setNodeState,
    id: flushBase.id,
    flowTriggerData: flushBase.flowTriggerData,
    flowId: flushBase.flowId,
    objectData: snapshot,
    setSchema: flushBase.setSchema,
    newEdges: flushBase.newEdges,
    changeType: 'default',
    connectData: undefined,
    deleteEdges: undefined,
    updateEdge: undefined,
    objectCallerData: undefined,
    setSaving: flushBase.setSaving,
    versionId: flushBase.versionId,
  });
};

const setPatchSyncForNode = (
  nodeId: string,
  phase: 'pending' | 'synced' | 'error',
) => {
  useFlowsStore.getState().setNodeAutosaveSync(nodeId, phase);
};

const shouldSendPatch = (changeType: string): boolean => {
  if (CRITICAL_CHANGE_TYPES.includes(changeType)) {
    return true;
  }
  const s = useFlowsStore.getState();
  return canUnlockNonCriticalPatch(
    Date.now(),
    s.flowCanvasPhase,
    s.flowCanvasReadyAtMs,
    s.flowCanvasUserGestureUnlock,
  );
};

export const nodeDataAutoSaveDynamic = (payload: AutoSavePayload) => {
  const {
    setEdges: _setEdges,
    setNodeState,
    id,
    flowTriggerData,
    flowId,
    objectData,
    setSchema,
    newEdges,
    changeType = 'default',
    connectData,
    deleteEdges,
    updateEdge,
    objectCallerData,
    setSaving,
    versionId: explicitVersionId,
  } = payload;

  const resolvedObjectData = objectData ?? {};

  const versionId =
    explicitVersionId ||
    new URLSearchParams(window.location.search).get('versionId') ||
    undefined;

  if (changeType === 'default') {
    if (!flowId) {
      return;
    }
    const nodeIdForSave = typeof id === 'string' ? id.trim() : '';
    if (!nodeIdForSave) {
      return;
    }
    const pKey = defaultPendingKey(flowId, nodeIdForSave);
    const gen = useFlowsStore.getState().flowLoadGeneration;
    let pending = defaultPendingByNode.get(pKey);
    if (!pending) {
      pending = {
        timer: null,
        mergedObjectData: {},
        generation: gen,
        flushArgs: null,
      };
      defaultPendingByNode.set(pKey, pending);
    }
    pending.mergedObjectData = {
      ...pending.mergedObjectData,
      ...resolvedObjectData,
    };
    pending.generation = gen;
    pending.flushArgs = {
      setNodeState,
      id,
      flowTriggerData,
      flowId,
      setSchema,
      newEdges,
      changeType: 'default',
      connectData,
      deleteEdges,
      updateEdge,
      objectCallerData,
      setSaving,
      versionId,
    };

    if (pending.timer) {
      clearTimeout(pending.timer);
    }

    pending.timer = setTimeout(() => {
      const current = defaultPendingByNode.get(pKey);
      if (!current || !current.flushArgs) {
        return;
      }
      const liveGen = useFlowsStore.getState().flowLoadGeneration;
      if (current.generation !== liveGen) {
        defaultPendingByNode.delete(pKey);
        return;
      }

      const merged = { ...current.mergedObjectData };
      const flushBase = current.flushArgs;
      defaultPendingByNode.delete(pKey);

      const { nds: ndsAfterHandle, dataChangedNodeIds } =
        applyHandleChangesToNodes({
          ...flushBase,
          objectData: merged,
          newEdges: flushBase.newEdges,
        });

      if (!flushBase.flowId) {
        return;
      }

      if (useVersionControlStore.getState().isProductionView) {
        return;
      }

      const trimmedId =
        typeof flushBase.id === 'string' ? flushBase.id.trim() : '';
      if (!trimmedId) {
        return;
      }

      const cacheKey = `${flushBase.flowId}:${trimmedId}:default`;
      if (!shouldSendPatch('default')) {
        const ce = saveCache.get(cacheKey);
        if (ce?.timer) {
          clearTimeout(ce.timer);
          ce.timer = null;
        }
        saveCache.set(cacheKey, {
          lastData: merged,
          lastServerAcknowledgedData: ce?.lastServerAcknowledgedData ?? null,
          timer: null,
          timestamp: Date.now(),
        });
        return;
      }

      const cachedEntry = saveCache.get(cacheKey);

      if (cachedEntry?.lastData && deepEqual(cachedEntry.lastData, merged)) {
        return;
      }

      if (cachedEntry?.timer) {
        clearTimeout(cachedEntry.timer);
      }

      const runSave = async () => {
        const entryAtPatchStart = saveCache.get(cacheKey);
        const revertPayload =
          entryAtPatchStart !== undefined &&
          entryAtPatchStart.lastServerAcknowledgedData !== null
            ? { ...entryAtPatchStart.lastServerAcknowledgedData }
            : null;

        setPatchSyncForNode(trimmedId, 'pending');

        const ok = await persistPrimaryAndPropagatedNodes({
          flowId: flushBase.flowId!,
          primaryId: trimmedId,
          primaryData: merged,
          primaryChangeType: 'default',
          nds: ndsAfterHandle,
          dataChangedNodeIds,
          versionId: flushBase.versionId,
          setSaving: flushBase.setSaving,
        });
        if (!ok) {
          setPatchSyncForNode(trimmedId, 'error');
          if (revertPayload !== null) {
            revertOptimisticNodeData(
              {
                setNodeState: flushBase.setNodeState,
                id: flushBase.id,
                flowTriggerData: flushBase.flowTriggerData,
                flowId: flushBase.flowId,
                setSchema: flushBase.setSchema,
                newEdges: flushBase.newEdges,
                setSaving: flushBase.setSaving,
                versionId: flushBase.versionId,
              },
              revertPayload,
            );
          }
          useNotificationStore.getState().setNotification({
            type: NotificationType.Error,
            message:
              'Could not save node changes. The canvas was restored to the last saved version.',
          });
          return;
        }
        setPatchSyncForNode(trimmedId, 'synced');

        const cacheEntry = saveCache.get(cacheKey);
        if (cacheEntry) {
          cacheEntry.lastData = merged;
          cacheEntry.lastServerAcknowledgedData = { ...merged };
          cacheEntry.timer = null;
          cacheEntry.timestamp = Date.now();
        } else {
          saveCache.set(cacheKey, {
            lastData: merged,
            lastServerAcknowledgedData: { ...merged },
            timer: null,
            timestamp: Date.now(),
          });
        }
        for (const nid of dataChangedNodeIds) {
          if (nid === trimmedId) {
            continue;
          }
          const n = ndsAfterHandle.find(x => x.id === nid);
          if (!n?.data || typeof n.data !== 'object') {
            continue;
          }
          const sk = `${flushBase.flowId}:${nid}:default`;
          const propagatedData = { ...(n.data as Record<string, unknown>) };
          saveCache.set(sk, {
            lastData: propagatedData,
            lastServerAcknowledgedData: { ...propagatedData },
            timer: null,
            timestamp: Date.now(),
          });
        }
      };

      void runSave();
    }, DEBOUNCE_DELAY);

    return;
  }

  const applyResult = applyHandleChangesToNodes({
    setNodeState,
    id,
    flowTriggerData,
    flowId,
    objectData: resolvedObjectData,
    setSchema,
    newEdges,
    changeType,
    connectData,
    deleteEdges,
    updateEdge,
    objectCallerData,
    setSaving,
    versionId,
  });

  if (!flowId) return;

  if (useVersionControlStore.getState().isProductionView) return;

  const nodeIdForSave = typeof id === 'string' ? id.trim() : '';
  if (!nodeIdForSave) {
    return;
  }

  const cacheKey = `${flowId}:${nodeIdForSave}:${changeType}`;
  const isCriticalChange = CRITICAL_CHANGE_TYPES.includes(changeType);

  const onSaveNodeData = async () => {
    const entryAtPatchStart = saveCache.get(cacheKey);
    const revertPayload =
      entryAtPatchStart !== undefined &&
      entryAtPatchStart.lastServerAcknowledgedData !== null
        ? { ...entryAtPatchStart.lastServerAcknowledgedData }
        : null;

    if (isCriticalChange) {
      const saving = {
        finished: false,
        lastSaved: new Date().toISOString(),
        status: 'loading',
        flowId: flowId,
      };

      setSaving(saving);
      setPatchSyncForNode(nodeIdForSave, 'pending');

      try {
        await FlowService.updateNodeData(flowId, {
          id: nodeIdForSave,
          data: { ...resolvedObjectData, isEmptyState: false },
          changeType,
          versionId,
        });

        setSaving({
          finished: true,
          lastSaved: new Date().toISOString(),
          status: 'saved',
          flowId: flowId,
        });
        setPatchSyncForNode(nodeIdForSave, 'synced');

        const cacheEntry = saveCache.get(cacheKey);
        if (cacheEntry) {
          cacheEntry.lastData = resolvedObjectData;
          cacheEntry.lastServerAcknowledgedData = { ...resolvedObjectData };
          cacheEntry.timestamp = Date.now();
        } else {
          saveCache.set(cacheKey, {
            lastData: resolvedObjectData,
            lastServerAcknowledgedData: { ...resolvedObjectData },
            timer: null,
            timestamp: Date.now(),
          });
        }
      } catch {
        setSaving({
          finished: false,
          lastSaved: new Date().toISOString(),
          status: 'error',
          flowId: flowId,
        });
        setPatchSyncForNode(nodeIdForSave, 'error');
        if (revertPayload !== null) {
          revertOptimisticNodeData(
            {
              setNodeState,
              id,
              flowTriggerData,
              flowId,
              setSchema,
              newEdges,
              setSaving,
              versionId,
            },
            revertPayload,
          );
        }
        useNotificationStore.getState().setNotification({
          type: NotificationType.Error,
          message:
            'Could not save node changes. The canvas was restored to the last saved version.',
        });
      }
      return;
    }

    setPatchSyncForNode(nodeIdForSave, 'pending');

    const ok = await persistPrimaryAndPropagatedNodes({
      flowId,
      primaryId: nodeIdForSave,
      primaryData: resolvedObjectData,
      primaryChangeType: changeType,
      nds: applyResult.nds,
      dataChangedNodeIds: applyResult.dataChangedNodeIds,
      versionId,
      setSaving,
    });
    if (!ok) {
      setPatchSyncForNode(nodeIdForSave, 'error');
      if (revertPayload !== null) {
        revertOptimisticNodeData(
          {
            setNodeState,
            id,
            flowTriggerData,
            flowId,
            setSchema,
            newEdges,
            setSaving,
            versionId,
          },
          revertPayload,
        );
      }
      useNotificationStore.getState().setNotification({
        type: NotificationType.Error,
        message:
          'Could not save node changes. The canvas was restored to the last saved version.',
      });
      return;
    }
    setPatchSyncForNode(nodeIdForSave, 'synced');

    const cacheEntry = saveCache.get(cacheKey);
    if (cacheEntry) {
      cacheEntry.lastData = resolvedObjectData;
      cacheEntry.lastServerAcknowledgedData = { ...resolvedObjectData };
      cacheEntry.timer = null;
      cacheEntry.timestamp = Date.now();
    } else {
      saveCache.set(cacheKey, {
        lastData: resolvedObjectData,
        lastServerAcknowledgedData: { ...resolvedObjectData },
        timer: null,
        timestamp: Date.now(),
      });
    }
    for (const nid of applyResult.dataChangedNodeIds) {
      if (nid === nodeIdForSave) {
        continue;
      }
      const n = applyResult.nds.find(x => x.id === nid);
      if (!n?.data || typeof n.data !== 'object') {
        continue;
      }
      const sk = `${flowId}:${nid}:default`;
      const propagatedData = { ...(n.data as Record<string, unknown>) };
      saveCache.set(sk, {
        lastData: propagatedData,
        lastServerAcknowledgedData: { ...propagatedData },
        timer: null,
        timestamp: Date.now(),
      });
    }
  };

  if (isCriticalChange) {
    if (!shouldSendPatch(changeType)) {
      return;
    }
    onSaveNodeData();
    return;
  }

  if (!shouldSendPatch(changeType)) {
    return;
  }

  const cachedEntry = saveCache.get(cacheKey);

  if (
    cachedEntry?.lastData &&
    deepEqual(cachedEntry.lastData, resolvedObjectData)
  ) {
    return;
  }

  if (cachedEntry?.timer) {
    clearTimeout(cachedEntry.timer);
  }

  const timer = setTimeout(() => {
    onSaveNodeData();
  }, DEBOUNCE_DELAY);

  if (cachedEntry) {
    cachedEntry.timer = timer;
    cachedEntry.timestamp = Date.now();
  } else {
    saveCache.set(cacheKey, {
      lastData: null,
      lastServerAcknowledgedData: null,
      timer: timer,
      timestamp: Date.now(),
    });
  }
};

export const nodePassOnlyText = {
  imageReaderNode: 'inputPreview',
  audioReaderNode: 'inputPreview',
  documentSummarizer: 'inputPreview',
  arrayNode: 'content',
  imageGenerator: 'inputPreview',
  mathFunction: 'varId',
  fluxBox: 'varId',
  libraryNode: 'varId',
  fileSave: 'varId',
  outputController: 'customFileName',
  thirdPartyIntegration: 'blobConfig',
};

export const nodePassOnlyTextRealHandleId = {
  imageReaderNode: 'inputPreview',
  audioReaderNode: 'inputPreview',
  documentSummarizer: 'inputPreview',
  arrayNode: 'content',
  imageGenerator: 'inputPreview',
  mathFunction: 'varId',
  fluxBox: 'varId',
  libraryNode: 'varId',
  fileSave: 'varId',
  thirdPartyIntegration: 'blobConfig',
};
